import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { showError, showSuccess, showWarning } from '../utils/toast';

const PgAutoCadastro = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        nome: '',
        cpf: '',
        email: '',
        telefone: '',
        nascimento: ''
    });
    const [isLoading, setIsLoading] = useState(false);

    const formatarCPF = (valor) => {
        const numeros = valor.replace(/\D/g, '');
        if (numeros.length === 11) {
            return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        return valor;
    };

    const formatarTelefone = (valor) => {
        const numeros = valor.replace(/\D/g, '');
        if (numeros.length === 11) {
            return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '$1 $2-$3');
        } else if (numeros.length === 10) {
            return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2-$3');
        }
        return numeros;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'cpf') {
            setFormData(prev => ({ ...prev, [name]: formatarCPF(value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleBlurTelefone = (e) => {
        const valor = e.target.value;
        setFormData(prev => ({ ...prev, telefone: formatarTelefone(valor) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validações básicas
        if (formData.cpf.replace(/\D/g, '').length !== 11) {
            showError('CPF inválido');
            return;
        }

        if (!formData.email.includes('@')) {
            showError('Email inválido');
            return;
        }

        if (formData.telefone.replace(/\D/g, '').length !== 11) {
            showError('Telefone inválido. Digite 11 dígitos (DDD + número)');
            return;
        }

        setIsLoading(true);

        try {
            // Verificar se CPF ou email já existem
            const verificarResponse = await fetch(
                `https://novo.faestal.com.br/cliente/verificar.php?cpf=${encodeURIComponent(formData.cpf)}&email=${encodeURIComponent(formData.email)}`
            );
            const verificarData = await verificarResponse.json();

            if (verificarData.status === 'exists') {
                const cliente = verificarData.cliente;
                
                // Verificar se o cliente não tem email cadastrado
                if (!cliente.email || cliente.email.trim() === '') {
                    showWarning('Você já possui cadastro, mas seu email não está registrado. Entre em contato conosco pelo WhatsApp para atualizar seus dados.');
                    
                    // Mostrar modal com número de WhatsApp
                    setTimeout(() => {
                        if (window.confirm('Deseja abrir o WhatsApp agora?\n\n📱 (21) 97962-1222\n\nClique em OK para abrir ou Cancelar para fechar.')) {
                            window.open('https://wa.me/5521979621222?text=Olá,%20preciso%20atualizar%20meu%20email%20no%20cadastro', '_blank');
                        }
                    }, 500);
                    
                    setIsLoading(false);
                    return;
                }
                
                // Redirecionar para página de usuário existente
                navigate('/usuario-existente', {
                    state: {
                        cpf: cliente.cpf,
                        email: cliente.email,
                        possuiSenha: cliente.possuiSenha
                    }
                });
                
                setIsLoading(false);
                return;
            }

            // Cliente não existe, criar novo
            const criarResponse = await fetch('https://novo.faestal.com.br/cliente/create.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const criarData = await criarResponse.json();

            if (criarData.status === 'success') {
                showSuccess('Cadastro realizado com sucesso!');
                
                // Enviar email de cadastro de senha automaticamente
                const idCliente = criarData.id_cliente || criarData.id;
                if (idCliente) {
                    await fetch(`https://novo.faestal.com.br/senha/enviar.php?id=${idCliente}`);
                    showSuccess('Link de cadastro de senha enviado para seu email!');
                }

                setTimeout(() => navigate('/'), 2000);
            } else {
                showError(criarData.message || 'Erro ao realizar cadastro');
            }
        } catch (error) {
            console.error('Erro:', error);
            showError('Erro ao processar cadastro');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mt-5">
            <div className="row justify-content-center">
                <div className="col-md-6">
                    <div className="card shadow">
                        <div className="card-header bg-primary text-white">
                            <h4 className="mb-0">Criar Conta</h4>
                        </div>
                        <div className="card-body">
                            <p className="text-muted mb-4">
                                Preencha os dados abaixo para criar sua conta no Portal FAEWorks.
                            </p>

                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label htmlFor="nome" className="form-label">
                                        Nome Completo <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="nome"
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="cpf" className="form-label">
                                        CPF <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="cpf"
                                        name="cpf"
                                        value={formData.cpf}
                                        onChange={handleChange}
                                        required
                                        placeholder="000.000.000-00"
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="email" className="form-label">
                                        Email <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="telefone" className="form-label">
                                        Telefone <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        className="form-control"
                                        id="telefone"
                                        name="telefone"
                                        value={formData.telefone}
                                        onChange={handleChange}
                                        onBlur={handleBlurTelefone}
                                        required
                                        placeholder="21 99999-9999"
                                        maxLength="13"
                                    />
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="nascimento" className="form-label">
                                        Data de Nascimento
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        id="nascimento"
                                        name="nascimento"
                                        value={formData.nascimento}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="d-grid gap-2">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-lg"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Processando...
                                            </>
                                        ) : (
                                            'Criar Conta'
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => navigate('/login')}
                                        disabled={isLoading}
                                    >
                                        Já tenho conta
                                    </button>
                                </div>
                            </form>

                            <div className="alert alert-info mt-4 mb-0">
                                <small>
                                    <strong>ℹ️ Importante:</strong> Após criar sua conta, você receberá um email com um link para cadastrar sua senha.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PgAutoCadastro;
